import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Info, AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { API_BASE_URL } from '@/config';
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLoader } from "@/contexts/LoaderContext";
import { toast } from "sonner";
import {
    useDigioSdk,
    getDigioConstructor,
    resolveDigioToken,
    removeOrphanedDigioOverlays,
    fitDigioOverlayToViewport,
    type DigioCallbackResponse,
    type DigioEvent,
    type DigioInstance,
} from "@/hooks/useDigioSdk";
import { useMandateStatusWatcher } from "@/hooks/useMandateStatusWatcher";
import { DIGIO_ENVIRONMENT } from "@/config";

/**
 * Digio eNACH events we subscribe to (entity: `nach`).
 *
 * BOTH spellings of the UPI cancel event are listed on purpose. Digio's own
 * event table writes it as `nach_upi_mandate.cancelled_by_user` (underscore
 * where every sibling has a dot) — the same kind of upstream typo as their
 * misspelled `DOCUEMNT_NOT_READY_FOR_THIS_ACTION` error code. We cannot tell
 * from the SDK bundle which form actually goes over the wire, and the filter
 * is an exact-match test (`event_filter.includes(event.data.event)`), so a
 * single wrong guess would silently swallow every UPI cancellation. Subscribing
 * to both costs nothing; the handler already keys off `endsWith("cancelled_by_user")`.
 *
 * Read-only — pass a fresh mutable copy (`[...MANDATE_EVENTS]`) to the SDK.
 */
const MANDATE_EVENTS: readonly string[] = [
    "nach.upi_mandate.vpa_verified",
    "nach.upi_mandate.authenticated",
    "nach.upi_mandate.already_authenticated",
    "nach.upi_mandate.rejected",
    "nach.upi_mandate.failed",
    "nach_upi_mandate.cancelled_by_user", // verbatim from Digio's table (sic)
    "nach.upi_mandate.cancelled_by_user", // dotted form, in case the table is wrong
    "nach.api_mandate.authenticated",
    "nach.api_mandate.already_authenticated",
    "nach.api_mandate.failed",
    "nach.api_mandate.cancelled_by_user",
] as const;

const upiSchema = z.object({
    vpa: z.string().min(3, "VPA is required").regex(/^[\w.-]+@[\w.-]+$/, "Invalid VPA format"),
});

type UpiFormValues = z.infer<typeof upiSchema>;

interface UPIMandateProps {
    value?: any;
    onChange?: (data: any) => void;
    onDone?: () => void;
    /** Called when UPI is skipped (terminal failure / 30s post-close timeout).
     *  Parent should unlock the NACH section. */
    onUpiSkipped?: () => void;
    isBuilderMode?: boolean;
    registerSubmit?: (submitFn: () => Promise<boolean>) => void;
}

/**
 * UPI Mandate Component — either-mandate logic
 *
 * Uses the Digio Web SDK v11 inline iframe (no raw <iframe src=...>).
 * Passes digio_token_id as the 3rd arg to digio.submit() so the gateway
 * skips its OTP screen entirely.
 *
 * While Digio overlay is open: poll every 4s indefinitely.
 * When Digio callback fires CANCELLED: start 30-second countdown; if UPI
 *   confirms → success; if 30s elapses with no success → skip UPI, call onUpiSkipped().
 * Terminal failure from backend (is_terminal_failure=true): skip immediately.
 */
const UPIMandate: React.FC<UPIMandateProps> = ({
    value,
    onChange,
    onDone,
    onUpiSkipped,
    isBuilderMode = false,
    registerSubmit
}) => {
    const { loading, setLoading, setLoaderMessage } = useLoader();
    const { loaded: sdkLoaded } = useDigioSdk();
    const [fetching, setFetching] = useState(true);
    const [isCompleted, setIsCompleted] = useState(false);
    const [customerInfo, setCustomerInfo] = useState({
        identifier: "",
        bankingName: ""
    });

    // Ref so async poll callbacks always read the latest customerInfo
    const customerInfoRef = useRef(customerInfo);
    useEffect(() => { customerInfoRef.current = customerInfo; }, [customerInfo]);

    // Refs for poll/countdown timers so we can clear them from any closure
    const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const isHandledRef = useRef(false);
    const postCloseSecondsRef = useRef(0);
    const hasLoadedRef = useRef(false); // Prevent duplicate API calls from React re-renders

    // Digio SDK instance ref — used to call cancel() on cleanup
    const digioRef = useRef<DigioInstance | null>(null);
    // runId guard — prevents phantom CANCELLED from a discarded Digio instance
    const runIdRef = useRef(0);
    // Cleanup returned by fitDigioOverlayToViewport — detaches resize listeners
    const overlayCleanupRef = useRef<(() => void) | null>(null);

    // Keep a stable ref to onDone so the load-effect closure always calls the
    // latest version even though the effect has an empty dependency array.
    const onDoneRef = useRef(onDone);
    useEffect(() => { onDoneRef.current = onDone; }, [onDone]);

    const upiForm = useForm<UpiFormValues>({
        resolver: zodResolver(upiSchema),
        mode: "onChange",
        defaultValues: {
            vpa: value?.vpa || "",
        },
    });

    useEffect(() => {
        // Prevent duplicate calls from React StrictMode or re-renders
        if (hasLoadedRef.current) return;
        hasLoadedRef.current = true;

        axios
            .post(`${API_BASE_URL}/api/get_verified_mandate_details/`)
            .then((res) => {
                if (res.status === 200) {
                    setCustomerInfo({
                        identifier: String(res.data.customer_identifier),
                        bankingName: res.data.cust_banking_name
                    });
                    if (res.data.vpa) {
                        upiForm.setValue("vpa", res.data.vpa);
                    }
                    if (res.data.upi_mandate_done) {
                        // UPI already confirmed by Digio (status=4) on a previous session.
                        // Mark complete locally and fire onDone() to unlock the NACH section
                        // (JourneyPage.upiSkipped is never initialised from backend state,
                        // so without this call the NACH button stays greyed out on reload).
                        setIsCompleted(true);
                        if (onChange) onChange({ completed: true, vpa: res.data.vpa });
                        toast.success("UPI Mandate already completed!");
                        if (onDoneRef.current) onDoneRef.current();
                    }
                }
            })
            .catch((err) => {
                console.error("Fetch error:", err);
                toast.error("Failed to fetch mandate details.");
            })
            .finally(() => setFetching(false));
    }, []);

    // ── Server-truth watcher ─────────────────────────────────────────────────
    // The bootstrap fetch above runs ONCE (hasLoadedRef + [] deps). UPI is the
    // worst case for that: Digio confirms UPI autopay ASYNCHRONOUSLY — it returns
    // 'partial' (user approved in their app, NPCI not yet done, typically 1-2
    // minutes) and only later flips to register_success. If the customer's tab was
    // backgrounded or discarded during that window — which mobile OSes do
    // routinely when the user switches to their UPI app — the in-card poll loop is
    // gone and nothing ever tells this card it succeeded.
    //
    // The background sweep (los_app.poll_pending_mandates_task) already writes
    // status=4 in that case; this watcher is how the UI finds out, on an interval
    // and immediately on tab focus (i.e. the instant the user comes back).
    const { status: serverMandateStatus } = useMandateStatusWatcher({
        enabled: !isBuilderMode && !isCompleted,
        // Stop only on a FINAL outcome. Stopping on `done` alone would leave a
        // revoked mandate ('3') being polled forever with nothing left to learn.
        stopWhen: (s) => s.upi.done || s.upi.status === '3',
    });

    // "Registration has started but is not resolved yet": Digio issued a
    // mandate_id, it is not confirmed, and it has not terminally failed (status
    // '3'). This matters more for UPI than NACH because UPI confirms
    // ASYNCHRONOUSLY — Digio sits on 'partial' for 1-2 minutes while NPCI decides,
    // and during that window the card would otherwise look completely idle and
    // invite the user to start over.
    const upiServer = serverMandateStatus?.upi;
    // Any mandate_id that is not yet confirmed counts as "something happened here",
    // INCLUDING a rejected/revoked one (status '3') — a dead mandate still needs an
    // explanation on screen, otherwise the card looks untouched and the user thinks
    // nothing happened.
    const upiInProgress = Boolean(upiServer?.mandate_id && !upiServer.done);
    // status '3' = rejected/revoked at Digio: show a retry prompt, not a spinner.
    const upiFailed = upiServer?.status === '3';

    useEffect(() => {
        // Read via the optional-chained local: `serverMandateStatus?.upi.done` still
        // throws if `upi` itself is missing (the ?. only guards the outer object),
        // which is exactly how an empty 304 body crashed this page in production.
        if (!upiServer?.done || isCompleted) return;

        setIsCompleted(true);
        // Mirrors the bootstrap path above: report completion AND call onDone so
        // JourneyPage unlocks the NACH section (upiSkipped is never initialised
        // from backend state, so without onDone the NACH button stays greyed out).
        if (onChange) onChange({ completed: true, vpa: upiForm.getValues("vpa") });
        toast.success("UPI Mandate confirmed!");
        if (onDoneRef.current) onDoneRef.current();
        // onChange / upiForm are intentionally omitted — JourneyPage passes an
        // inline arrow for onChange, so including it would re-run this on every
        // parent render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverMandateStatus, isCompleted]);

    // Clean up timers and Digio instance on unmount
    useEffect(() => {
        return () => {
            if (overlayCleanupRef.current) {
                overlayCleanupRef.current();
                overlayCleanupRef.current = null;
            }
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
            if (countdownRef.current) clearInterval(countdownRef.current);
            if (digioRef.current) {
                try { digioRef.current.cancel(); } catch { /* ignore */ }
                digioRef.current = null;
            }
        };
    }, []);

    /**
     * Calls skip_upi_mandate/ on the backend (advances UPI_MANDATE → ENACH_MANDATE),
     * then notifies the parent so the NACH section unlocks.
     */
    const skipUpiAndUnlockNach = async () => {
        try {
            await axios.post(`${API_BASE_URL}/api/skip_upi_mandate/`);
        } catch (err) {
            console.warn("[UPIMandate] skip_upi_mandate call failed:", err);
        }
        if (onUpiSkipped) onUpiSkipped();
    };

    /**
     * Called ONLY when backend returns is_terminal_failure=true OR
     * 30s post-close countdown expires with no success.
     * Closes Digio overlay, skips UPI, notifies parent to unlock NACH.
     */
    const handleUpiTerminalFailure = useCallback(async () => {
        if (isHandledRef.current) return;
        isHandledRef.current = true;
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);

        if (digioRef.current) {
            try { digioRef.current.cancel(); } catch { /* ignore */ }
            digioRef.current = null;
        }
        removeOrphanedDigioOverlays();

        // Force body scroll restoration (in case cleanup didn't run)
        try {
            document.body.style.removeProperty("overflow");
            // Force reflow to ensure scroll is restored
            void document.body.offsetHeight;
        } catch { /* ignore */ }

        setLoading(true);
        setLoaderMessage("mandate");
        toast.info("UPI mandate was not completed. Unlocking NACH mandate…");
        try {
            await skipUpiAndUnlockNach();
        } finally {
            setLoaderMessage(null);
            setLoading(false);
        }
    }, []);

    /**
     * Called when backend confirms is_success=true.
     * Closes Digio overlay, marks UPI as completed, notifies parent.
     */
    const handleMandateSuccess = useCallback(async () => {
        if (isHandledRef.current) return;
        isHandledRef.current = true;
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);

        if (digioRef.current) {
            try { digioRef.current.cancel(); } catch { /* ignore */ }
            digioRef.current = null;
        }
        removeOrphanedDigioOverlays();

        // Force body scroll restoration (in case cleanup didn't run)
        try {
            document.body.style.removeProperty("overflow");
            // Force reflow to ensure scroll is restored
            void document.body.offsetHeight;
        } catch { /* ignore */ }

        setLoading(true);
        setLoaderMessage("mandate");
        toast.success("UPI Mandate verified successfully!");
        setIsCompleted(true);
        if (onChange) onChange({ completed: true, vpa: upiForm.getValues('vpa') });
        if (onDone) onDone();
        setLoaderMessage(null);
        setLoading(false);
    }, [onChange, onDone, upiForm]);

    /**
     * Start polling the verify endpoint - Sequential polling (wait for response before next request)
     * Called once after the Digio overlay opens.
     */
    const startPolling = useCallback((runId: number) => {
        const endpoint = `${API_BASE_URL}/api/caprion/digio/verify-upi-mandate/`;
        
        const poll = async () => {
            if (isHandledRef.current || runId !== runIdRef.current) {
                if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
                return;
            }
            
            try {
                const statusCheck = await axios.post(endpoint);
                if (statusCheck.data.is_success === true) {
                    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
                    await handleMandateSuccess();
                    return;
                }
                if (statusCheck.data.is_terminal_failure === true) {
                    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
                    await handleUpiTerminalFailure();
                    return;
                }
                // Still pending — schedule next poll after 4 seconds
                pollTimerRef.current = setTimeout(poll, 4000);
            } catch (_) {
                // Error — retry after 4 seconds
                pollTimerRef.current = setTimeout(poll, 4000);
            }
        };
        
        // Start first poll immediately
        poll();
    }, [handleMandateSuccess, handleUpiTerminalFailure]);

    /**
     * Called when the user SUBMITS the UPI form inside the Digio iframe
     * (Digio callback fires with no error_code, or event "authenticated").
     * UPI autopay mandates are processed asynchronously — poll for up to
     * 2 minutes before giving up and skipping to NACH.
     * Do NOT declare success here — only the poll can do that.
     */
    const handleUpiFormSubmitted = useCallback((runId: number) => {
        if (isHandledRef.current || runId !== runIdRef.current) return;

        const MAX_POLL_SECONDS = 120;
        postCloseSecondsRef.current = MAX_POLL_SECONDS;
        setLoading(true);
        setLoaderMessage("mandate");
        toast.info("UPI form submitted. Waiting for confirmation…");

        // Start polling NOW — user has interacted with Digio popup.
        // Polling was intentionally NOT started on digio.submit() to avoid
        // auto-succeeding before the user does anything (Digio may return
        // 'partial' for a previously registered VPA immediately).
        startPolling(runId);

        countdownRef.current = setInterval(() => {
            if (isHandledRef.current) {
                if (countdownRef.current) clearInterval(countdownRef.current);
                return;
            }
            postCloseSecondsRef.current -= 1;
            if (postCloseSecondsRef.current <= 0) {
                if (countdownRef.current) clearInterval(countdownRef.current);
                if (pollTimerRef.current) clearInterval(pollTimerRef.current);
                // 2 minutes elapsed — skip UPI and unlock NACH
                void handleUpiTerminalFailure();
            }
        }, 1000);
    }, [handleUpiTerminalFailure, startPolling]);

    /**
     * Called when Digio fires CANCELLED (user closed the overlay WITHOUT
     * completing the form). Starts 30-second post-close countdown; poll
     * keeps running. If UPI confirms within 30s → success; else → skip to NACH.
     */
    const handleDigioCancelled = useCallback((runId: number) => {
        if (isHandledRef.current || runId !== runIdRef.current) return;

        const UPI_POST_CLOSE_SECONDS = 30;
        postCloseSecondsRef.current = UPI_POST_CLOSE_SECONDS;
        setLoading(true);
        setLoaderMessage("mandate");
        toast.info("UPI window closed. Checking status for 30 more seconds…");

        // Start polling NOW — user closed the Digio popup (may have completed
        // the UPI flow in their banking app before closing). Poll for 30s.
        startPolling(runId);

        countdownRef.current = setInterval(() => {
            if (isHandledRef.current) {
                if (countdownRef.current) clearInterval(countdownRef.current);
                return;
            }
            postCloseSecondsRef.current -= 1;
            if (postCloseSecondsRef.current <= 0) {
                if (countdownRef.current) clearInterval(countdownRef.current);
                if (pollTimerRef.current) clearInterval(pollTimerRef.current);
                void handleUpiTerminalFailure();
            }
        }, 1000);
    }, [handleUpiTerminalFailure, startPolling]);

    const handleUpiSubmit = async (values: UpiFormValues) => {
        setLoading(true);
        setLoaderMessage("mandate");

        // New attempt — invalidate callbacks from any previous Digio instance
        const runId = ++runIdRef.current;
        isHandledRef.current = false;
        postCloseSecondsRef.current = 0;
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);
        if (digioRef.current) {
            try { digioRef.current.cancel(); } catch { /* ignore */ }
            digioRef.current = null;
        }
        removeOrphanedDigioOverlays();

        // ── Initialise Digio SDK inline (must happen inside the click handler) ──
        const DigioCtor = getDigioConstructor();
        let digio: DigioInstance | null = null;
        let inlineReady = false;

        if (!sdkLoaded || !DigioCtor) {
            console.warn("[UPIMandate] Digio SDK not ready — will fall back to redirect_url.");
        } else {
            try {
                digio = new DigioCtor({
                    environment: DIGIO_ENVIRONMENT,
                    logo: `${window.location.origin}/logo.svg`,
                    theme: { primaryColor: "#065A4E", secondaryColor: "#000000" },
                    is_iframe: true,
                    callback: (response: DigioCallbackResponse) => {
                        console.log("[UPIMandate] Digio callback:", response);
                        if (runId !== runIdRef.current) {
                            console.warn("[UPIMandate] Ignoring stale Digio callback:", response);
                            return;
                        }
                        if (response?.error_code) {
                            if (response.error_code === "CANCELLED") {
                                // User closed the overlay — keep polling for async confirmation
                                handleDigioCancelled(runId);
                            } else {
                                // Hard error from Digio (not just a close)
                                void handleUpiTerminalFailure();
                            }
                            return;
                        }
                        // Digio callback fires when the user SUBMITS the form in the iframe,
                        // NOT when the mandate is confirmed by the bank. UPI mandates are
                        // processed asynchronously — the backend verify endpoint must return
                        // is_success:true before we can declare success. Poll for up to 2 min.
                        console.log("[UPIMandate] Digio callback success (form submitted) — waiting for backend confirmation via poll.");
                        handleUpiFormSubmitted(runId);
                    },
                    event_listener: (event: DigioEvent) => {
                        if (runId !== runIdRef.current) return;
                        const name = event?.event ?? "";
                        console.log("[UPIMandate] Digio event:", name);
                        if (name.endsWith("authenticated") || name.endsWith("already_authenticated")) {
                            // Same as callback success — form submitted, mandate is async.
                            // Do NOT call handleMandateSuccess here; let the poll confirm it.
                            console.log("[UPIMandate] Digio event authenticated — waiting for backend confirmation via poll.");
                            handleUpiFormSubmitted(runId);
                            return;
                        }
                        if (name.endsWith("cancelled_by_user")) {
                            handleDigioCancelled(runId);
                            return;
                        }
                        if (name.endsWith("rejected") || name.endsWith("failed")) {
                            void handleUpiTerminalFailure();
                        }
                    },
                    // Fresh copy per instance — SDK mutates the array in place
                    event_filter: [...MANDATE_EVENTS],
                });
                digio.init();
                digioRef.current = digio;
                // Size the overlay to the visual viewport so it fits on mobile
                if (overlayCleanupRef.current) overlayCleanupRef.current();
                overlayCleanupRef.current = fitDigioOverlayToViewport("UPI mandate");
                inlineReady = true;
            } catch (initError) {
                console.warn("[UPIMandate] Digio init() threw:", initError);
                digio = null;
                digioRef.current = null;
                removeOrphanedDigioOverlays();
            }
        }

        try {
            const payload = {
                customer_identifier: customerInfo.identifier,
                cust_banking_name: customerInfo.bankingName,
                vpa: values.vpa
            };
            const response = await axios.post(`${API_BASE_URL}/api/create_digio_upi_mandate/`, payload);

            if (response.status !== 200 || !response.data) {
                throw new Error("Mandate setup could not be initiated.");
            }

            const mandateId: string | null = response.data.mandate_id ?? null;
            const identifier: string = response.data.customer_identifier || customerInfo.identifier;
            const redirectUrl: string | null = response.data.redirect_url || null;
            const tokenId = resolveDigioToken(
                response.data.digio_token_id,
                response.data.digio_token_valid_till,
                "UPI mandate",
            );

            if (inlineReady && digio && mandateId) {
                // ── SDK path: submit inline with token (skips OTP) ──────────────
                try {
                    digio.submit(mandateId, identifier, tokenId);
                } catch (submitError) {
                    console.warn("[UPIMandate] digio.submit() threw:", submitError);
                    // Fall through to redirect fallback below
                    inlineReady = false;
                }
            }

            if (!inlineReady || !digio || !mandateId) {
                // ── Fallback: open Digio's own authentication_url ───────────────
                if (digioRef.current) {
                    try { digioRef.current.cancel(); } catch { /* ignore */ }
                    digioRef.current = null;
                }
                removeOrphanedDigioOverlays();

                if (redirectUrl) {
                    console.warn("[UPIMandate] Falling back to redirect_url:", redirectUrl);
                    window.open(redirectUrl, "_blank", "noopener,noreferrer");
                    startPolling(runId);
                    setLoaderMessage(null);
                    setLoading(false);
                } else {
                    toast.error("Mandate setup could not be initiated. Please try again.");
                    setLoaderMessage(null);
                    setLoading(false);
                }
                return;
            }

            // SDK overlay is now covering the viewport — hide our loader.
            // DO NOT start polling here. Polling is started only after the user
            // interacts with the Digio popup (handleUpiFormSubmitted or
            // handleDigioCancelled). Starting it here would cause the first poll
            // to fire immediately after digio.submit(), before the user has done
            // anything — and since Digio may return 'partial' for a previously
            // registered VPA, the mandate would auto-succeed without user action.
            setLoaderMessage(null);
            setLoading(false);

        } catch (err: any) {
            if (digioRef.current) {
                try { digioRef.current.cancel(); } catch { /* ignore */ }
                digioRef.current = null;
            }
            removeOrphanedDigioOverlays();
            toast.error(err.response?.data?.error || "An error occurred initiating UPI mandate.");
            setLoaderMessage(null);
            setLoading(false);
        }
    };

    if (fetching) return null;

    return (
        <div className={`border rounded-xl p-6 transition-all duration-500 relative ${isCompleted ? "border-intellend-green/20 bg-intellend-green/5 overflow-hidden" : "border-intellend-green/30 bg-white shadow-sm"}`}>
            {isCompleted && (
                <div className="absolute inset-0 z-10 bg-white/60 backdrop-blur-[2px] rounded-xl flex items-center justify-center">
                    <div className="flex items-center gap-2 text-intellend-green font-bold text-lg bg-white px-4 py-2 rounded-full shadow-sm border border-intellend-green/10">
                        <CheckCircle className="h-6 w-6 text-intellend-green" />
                        UPI Mandate Registered
                    </div>
                </div>
            )}

            {/* IN-PROGRESS CHIP — inline with the heading; see the matching block in
                NACHMandate.tsx. Matters more here because UPI confirms
                ASYNCHRONOUSLY: Digio sits on 'partial' for 1-2 min while NPCI
                decides, and the card would otherwise look idle and invite a restart.
                Hidden on terminal failure (status '3') so a dead mandate never spins
                forever. */}
            <h2 className={`text-xl font-bold text-gray-800 ${
                !isCompleted && upiInProgress ? "" : "mb-4"
            }`}>
                1. UPI Autopay Mandate
            </h2>

            {!isCompleted && upiInProgress && (
                upiFailed ? (
                    <p className="mt-1 mb-4 flex items-center gap-1.5 text-xs text-red-600">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                        Previous mandate failed — please set it up again or skip to NACH.
                    </p>
                ) : (
                    <p className="mt-1 mb-4 flex items-center gap-1.5 text-xs text-blue-700">
                        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                        Verifying… approved in your UPI app, waiting for bank confirmation.
                    </p>
                )
            )}

            <div className="space-y-3 mb-6">
                <div>
                    <p className="text-sm font-semibold text-gray-700">Customer Identifier (Phone)</p>
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 mt-1 text-gray-600">
                        {customerInfo.identifier || "-"}
                    </div>
                </div>
                <div>
                    <p className="text-sm font-semibold text-gray-700">Customer Banking Name</p>
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 mt-1 text-gray-600">
                        {customerInfo.bankingName || "-"}
                    </div>
                </div>
            </div>

            <Form {...upiForm}>
                <form onSubmit={upiForm.handleSubmit(handleUpiSubmit)} className="space-y-4">
                    <FormField
                        control={upiForm.control}
                        name="vpa"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-gray-700 font-semibold flex items-center gap-2">
                                    UPI VPA (Virtual Private Address) <span className="text-red-500">*</span>
                                </FormLabel>
                                <FormControl>
                                    <Input
                                        placeholder="e.g. mobile@upi"
                                        {...field}
                                        className="border-intellend-green/30 focus-visible:ring-intellend-green h-12 text-lg"
                                        disabled={isCompleted}
                                    />
                                </FormControl>
                                <div className="flex items-start gap-2 text-xs text-blue-600 mt-1">
                                    <Info size={14} className="mt-0.5" />
                                    <span>Example: 9876543210@upi, john.doe@okaxis, etc.</span>
                                </div>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg flex gap-3 text-sm text-amber-800">
                        <AlertCircle className="h-5 w-5 shrink-0" />
                        <p>By clicking "Setup Mandate", you agree to authorize automatic repayments via UPI.</p>
                    </div>
                    <Button
                        type="submit"
                        className="w-full bg-intellend-green hover:bg-intellend-green/90 h-12 text-lg font-bold shadow-md transition-all active:scale-[0.98]"
                        disabled={loading || !upiForm.formState.isValid || isCompleted}
                    >
                        {loading ? "Processing..." : "Setup UPI Mandate"}
                    </Button>
                </form>
            </Form>

            {/* Skip UPI — user can bypass UPI and go directly to NACH mandate */}
            {!isCompleted && (
                <div className="mt-3 text-center">
                    <button
                        type="button"
                        onClick={async () => {
                            if (loading) return;
                            setLoading(true);
                            setLoaderMessage("mandate");
                            try {
                                await skipUpiAndUnlockNach();
                            } finally {
                                setLoaderMessage(null);
                                setLoading(false);
                            }
                        }}
                        disabled={loading}
                        className="text-sm text-gray-400 hover:text-gray-600 underline underline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Skip UPI and proceed to NACH mandate
                    </button>
                </div>
            )}

            <p className="text-center text-xs text-gray-400 mt-4 italic">Securely powered by Digio</p>
        </div>
    );
};

export default UPIMandate;
